import { Folder } from '@nextforce/api'
import { useTheme } from '@rneui/themed'
import { useRoutingContext } from 'app/routing_context'
import { useConversation } from 'chat/conversations/queries/detail'
import { isRepository } from 'chat/conversations/utils'
import { useFileFolderActions } from 'file_folder_repository/context'
import { useChatGroupAllFileFolders } from 'file_folder_repository/queries/list'
import { useChatGroupMedia } from 'file_folder_repository/queries/media'
import {
  filterResourceList,
  getFileFolderList,
  sortResourceList,
} from 'file_folder_repository/util'
import { getFilterItems } from 'file_folder_repository/views/filters_row'
import { FileFolderSearchScreenProps } from 'home/stack/types'
import { useCallback, useLayoutEffect, useMemo, useState } from 'react'
import { Keyboard } from 'react-native'
import { showHeaderButton } from 'shared/header/header_button'
import { FileFolderSearchView } from './views'

export function FileFolderSearchScreen({
  navigation,
  route,
}: FileFolderSearchScreenProps) {
  const { theme } = useTheme()
  const { conversationId } = route.params

  const [searchText, setSearchText] = useState('')
  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0)
  const {
    onPressFile,
    sortOption,
    onLongPressResource,
    openSortOrderBottomSheet,
  } = useFileFolderActions()
  const { handleNavigation } = useRoutingContext()

  const conversationQuery = useConversation(conversationId)
  const allFileFoldersQuery = useChatGroupAllFileFolders(conversationId)
  const chatGroupMediaQuery = useChatGroupMedia(conversationId)

  const filterItems = getFilterItems(isRepository(conversationQuery?.data))

  const filteredData = useMemo(
    () =>
      filterResourceList(
        sortResourceList(
          [
            ...getFileFolderList(allFileFoldersQuery.data),
            ...(chatGroupMediaQuery.data ? chatGroupMediaQuery.data : []),
          ],
          sortOption
        ),
        selectedFilterIndex
      ).filter(resource =>
        resource.name.toLowerCase().includes(searchText.toLowerCase())
      ),
    [
      allFileFoldersQuery.data,
      chatGroupMediaQuery.data,
      sortOption,
      selectedFilterIndex,
      searchText,
    ]
  )

  const onPressFolder = useCallback(
    (folder: Folder) => {
      const path = [...(folder.path ?? [])]
      path?.push({ id: folder.id, name: folder.name })

      handleNavigation({
        routeName: 'file_folders_bottom_tab',
        routeParams: {
          screen: 'files',
          params: { conversationId, path },
          conversationId,
        },
      })
      setSelectedFilterIndex(0)
    },
    [conversationId, handleNavigation]
  )

  useLayoutEffect(() => {
    showHeaderButton({
      navigation,
      onPress: () => {
        Keyboard.dismiss()
        openSortOrderBottomSheet()
      },
      useIcon: true,
      title: 'sort',
      iconType: 'material-icons',
      theme,
    })
  }, [navigation, openSortOrderBottomSheet, theme])

  return (
    <FileFolderSearchView
      resources={filteredData}
      onPressFile={onPressFile}
      selectedFilterIndex={selectedFilterIndex}
      setSelectedFilterIndex={setSelectedFilterIndex}
      filterItems={filterItems}
      loading={allFileFoldersQuery.isLoading}
      onLongPressResource={onLongPressResource}
      onPressFolder={onPressFolder}
      searchTerm={searchText}
      setSearchTerm={setSearchText}
      sortOption={sortOption}
    />
  )
}
